import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PublicOrgRouteShell } from '@/components/public/PublicOrgRouteShell';
import { useOrgPublicProfile } from '@/contexts/OrgPublicProfileContext';
import { useOrgPaths } from '@/hooks/useOrgPaths';
import { useAuthStore } from '@/store/auth';
import { buildOrgLoginNavigationState } from '@/lib/orgLoginNavigation';

function isOrgScopedReturnUrl(url: string, orgBase: string, orgSlug: string): boolean {
  if (url.startsWith(`/club-academy/${orgSlug}`)) return true;
  if (url === orgBase || url.startsWith(`${orgBase}/`)) return true;
  return false;
}

function OrgLoginModalRedirectBody() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { p } = useOrgPaths();
  const { orgSlug } = useOrgPublicProfile();
  const { isAuthenticated, user, isInitialized, isLoading } = useAuthStore();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current || !p || !orgSlug || !isInitialized || isLoading) return;
    ran.current = true;

    const home = p('');
    if (!home) return;

    const returnUrl = searchParams.get('returnUrl');
    const postLoginDestination =
      returnUrl && isOrgScopedReturnUrl(returnUrl, home, orgSlug) ? returnUrl : home;

    if (isAuthenticated && user) {
      navigate(postLoginDestination, { replace: true });
      return;
    }

    // Hand off modal open to the destination page's OrgLoginDialogProvider via router state.
    // Opening here then navigating would unmount this route's provider and close the modal.
    navigate(home, {
      replace: true,
      state: buildOrgLoginNavigationState(postLoginDestination),
    });
  }, [
    navigate,
    orgSlug,
    p,
    searchParams,
    isAuthenticated,
    user,
    isInitialized,
    isLoading,
  ]);

  return null;
}

/** Replaces full-page org login — opens the modal and returns to the org home or return URL. */
export default function OrgLoginModalRedirect() {
  return (
    <PublicOrgRouteShell>
      <OrgLoginModalRedirectBody />
    </PublicOrgRouteShell>
  );
}
